import { CheckIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '../../helpers/classBuilders';
import { PropsWithClassName } from '../../types/common';
import { InputField } from '../InputField';
import { Stack } from '../Stack';

// `!` suffix required: the label's font-size comes from the unlayered
// field-label SCSS mixin, which beats un-important Tailwind utilities.
const labelSizeClasses = {
  sm: 'text-sm!',
  md: 'text-base!',
  lg: 'text-lg!',
} as const;

interface ToggleGroupProps {
  children: ReactNode;
  label?: string;
  labelSize?: keyof typeof labelSizeClasses;
  isLoading?: boolean;
  fullWidth?: boolean;
}

// Lets each Item stretch (flex-1) and center its content when the group is
// fullWidth, without the consumer wiring the prop on every Item.
const ToggleGroupContext = createContext<{ fullWidth: boolean }>({
  fullWidth: false,
});

interface ToggleGroupItemProps {
  value: string | number;
  checked: boolean;
  onChange: (value: string | number) => void;
  children: ReactNode;
  disabled?: boolean;
  showIcon?: boolean;
}

const ToggleGroupRoot = memo(
  ({
    children,
    className,
    label,
    labelSize = 'sm',
    isLoading,
    fullWidth = false,
  }: PropsWithClassName<ToggleGroupProps>) => {
    const groupId = useId();
    const labelId = `${groupId}-label`;
    const containerRef = useRef<HTMLDivElement>(null);
    const [indicatorStyle, setIndicatorStyle] = useState({
      width: 0,
      left: 0,
      opacity: 0,
    });
    // Transitions stay off while mounting: measuring with
    // getBoundingClientRect flushes styles at {width: 0}, which becomes the
    // "before-change style", so even a pre-paint position update would
    // animate. They are enabled one paint later (with unchanged values,
    // adding the class animates nothing), so only real moves transition.
    const [transitionEnabled, setTransitionEnabled] = useState(false);

    useEffect(() => {
      setTransitionEnabled(true);
    }, []);

    // useLayoutEffect so the first measurement lands before paint — with
    // useEffect the indicator gets painted at {width: 0, left: 0} first and
    // then visibly slides into place on every mount.
    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      const updateIndicator = () => {
        const checkedButton = container.querySelector(
          '[data-checked="true"]',
        ) as HTMLElement;

        if (checkedButton) {
          const containerRect = container.getBoundingClientRect();
          const buttonRect = checkedButton.getBoundingClientRect();

          setIndicatorStyle({
            width: buttonRect.width,
            left: buttonRect.left - containerRect.left,
            opacity: 1,
          });
        }
      };

      updateIndicator();

      // jsdom (unit tests) has no ResizeObserver
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      // AnimatePresence keeps the previous item's check icon mounted while it
      // exits, so measuring only on [children] captures a stale layout. The
      // observer re-measures once the icon actually mounts/unmounts. fullWidth
      // items also resize with the container, so layout changes re-measure too.
      const observer = new ResizeObserver(updateIndicator);
      observer.observe(container);
      Array.from(container.children).forEach(child => {
        observer.observe(child);
      });

      return () => {
        observer.disconnect();
      };
    }, [children]);

    return (
      <ToggleGroupContext.Provider value={{ fullWidth }}>
        <div>
          {label && (
            <InputField.Label
              id={labelId}
              className={cn(
                'block text-gray-900!',
                labelSizeClasses[labelSize],
              )}
              htmlFor=""
            >
              {label}
            </InputField.Label>
          )}
          <div
            ref={containerRef}
            className={cn(
              // h-10 on the container (not the items): the border lives here,
              // so border-box makes the whole group 40px; items stretch into
              // the remaining 38px.
              'relative inline-flex items-stretch h-10 border border-gray-300 rounded-lg bg-white',
              className,
              {
                'opacity-50 cursor-not-allowed': isLoading,
                'w-full': fullWidth,
              },
            )}
            aria-orientation="horizontal"
            role="tablist"
            aria-label={label || 'Toggle Group'}
            aria-labelledby={label ? labelId : undefined}
          >
            {/* Animated indicator background */}
            <div
              className={cn(
                'absolute top-0 bottom-0 bg-transparent rounded-lg pointer-events-none outline-2 outline-gray-900',
                transitionEnabled
                  ? 'transition-all duration-300 ease-out'
                  : 'transition-none',
              )}
              style={{
                width: `${indicatorStyle.width}px`,
                transform: `translateX(${indicatorStyle.left}px)`,
                opacity: indicatorStyle.opacity,
              }}
            />

            {children}
          </div>
        </div>
      </ToggleGroupContext.Provider>
    );
  },
);

const ToggleGroupItem = memo(
  ({
    value,
    checked,
    onChange,
    children,
    className,
    disabled = false,
    showIcon = false,
  }: PropsWithClassName<ToggleGroupItemProps>) => {
    const { fullWidth } = useContext(ToggleGroupContext);

    return (
      <button
        type="button"
        role="tab"
        className={cn(
          'relative flex items-center px-3 text-gray-500 cursor-pointer transition-colors duration-200 z-10',
          'first:rounded-l-lg last:rounded-r-lg focus-visible:focus-ring-form-inset',
          {
            'font-medium text-gray-900': checked,
            'hover:text-gray-900 focus-visible:text-gray-900':
              !checked && !disabled,
            'cursor-not-allowed opacity-50': disabled,
            // The content sits in an auto-width Stack, so it has to be
            // centered here — `justify` on that Stack would center nothing.
            'flex-1 justify-center': fullWidth,
          },
          className,
        )}
        onClick={() => onChange(value)}
        aria-selected={checked}
        id={`toggle_${value}`}
        aria-controls={`panel_${value}`}
        data-checked={checked}
        disabled={disabled}
      >
        <Stack direction="row" align="center" gap={1}>
          {/* initial={false}: an icon already checked at mount renders in
              place instead of replaying its enter fade on every re-mount.
              Only post-mount check changes animate. */}
          <AnimatePresence initial={false}>
            {checked && showIcon && (
              <motion.div
                className="text-gray-900"
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { duration: 0.3, delay: 0.05 },
                }}
                exit={{
                  opacity: 0,
                  transition: { duration: 0 },
                }}
              >
                <CheckIcon size={16} weight="bold" />
              </motion.div>
            )}
          </AnimatePresence>
          {children}
        </Stack>
      </button>
    );
  },
);

ToggleGroupRoot.displayName = 'ToggleGroup';
ToggleGroupItem.displayName = 'ToggleGroup.Item';

export const ToggleGroup = Object.assign(ToggleGroupRoot, {
  Item: ToggleGroupItem,
});
